/**
 * ModeToggle Component
 *
 * Shared toggle component for switching between Edit (Chat) and Audit modes.
 * Integrates with JotAudit introduction experiment to conditionally show diamond icon.
 */

import React, { useCallback, useMemo } from 'react';
import { SegmentedControl } from '@components/primitives/SegmentedControl';
import { useJotAuditIntroExperiment } from '@features/jotaudit';
import { Diamond as DiamondIcon } from '@mui/icons-material';
import { Badge, Box } from '@mui/material';

import type { SidebarMode } from './SidebarContainer';

export interface ModeToggleProps {
  /**
   * Current mode (chat, audit, or analysis)
   */
  mode: SidebarMode;

  /**
   * Number of audit issues to display in badge
   */
  auditIssueCount: number;

  /**
   * Callback when mode changes
   */
  onChange: (event: React.MouseEvent<HTMLElement>, newMode: SidebarMode | null) => void;

  /**
   * Minimum width for tabs (optional, defaults to 90)
   */
  minWidth?: number;

  /**
   * Size of the segmented control
   */
  size?: 'small' | 'medium' | 'large';

  /**
   * Whether to show the analysis mode option
   */
  showAnalysisMode?: boolean;

  /**
   * Whether to show the chat (Edit) mode option.
   * When false, the Edit tab is hidden (e.g. on completed/signed notes).
   * @default true
   */
  showChatMode?: boolean;
}

/**
 * ModeToggle - Shared component for Edit/Audit/Analysis mode switching
 *
 * Features:
 * - SegmentedControl for mode switching
 * - Badge showing audit issue count on the Audit tab
 * - Conditional diamond icon (experiment-driven)
 * - Optional Analysis mode
 * - Configurable minimum width
 */
export const ModeToggle: React.FC<ModeToggleProps> = ({
  mode,
  auditIssueCount,
  onChange,
  minWidth = 90,
  size = 'small',
  showAnalysisMode = false,
  showChatMode = true,
}) => {
  const { showDiamondIcon } = useJotAuditIntroExperiment();

  // Segmented control options for Edit/Audit/Analysis toggle
  const segmentedOptions = useMemo(
    () => [
      ...(showChatMode
        ? [
            {
              value: 'chat',
              label: 'Edit',
            },
          ]
        : []),
      {
        value: 'audit',
        label: (
          <Badge
            badgeContent={auditIssueCount}
            color="error"
            max={99}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.65rem',
                height: 16,
                minWidth: 16,
                // Float the pill just past the top-right corner so it clears
                // both the tab boundary and the adjacent diamond icon instead
                // of overlapping them. `pr` on the inner Box reserves the
                // horizontal room the pill floats into. z-index keeps it above
                // the sliding tab indicator; pointer-events lets clicks fall
                // through to the tab underneath.
                right: -4,
                top: -4,
                lineHeight: 'normal',
                zIndex: 3,
                pointerEvents: 'none',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 0.5 }}>
              <span>Audit</span>
              {showDiamondIcon && (
                <DiamondIcon
                  sx={{
                    fontSize: 20,
                    color: 'primary.main',
                  }}
                />
              )}
            </Box>
          </Badge>
        ),
      },
      ...(showAnalysisMode
        ? [
            {
              value: 'analysis',
              label: 'Analysis',
            },
          ]
        : []),
    ],
    [showDiamondIcon, showAnalysisMode, showChatMode, auditIssueCount],
  );

  // Adapter for SegmentedControl onChange
  const handleChange = useCallback(
    (newMode: string) => {
      onChange({} as React.MouseEvent<HTMLElement>, newMode as SidebarMode);
    },
    [onChange],
  );

  return (
    <Box
      sx={{
        '& .MuiTab-root': {
          minWidth,
        },
      }}
    >
      <SegmentedControl
        options={segmentedOptions}
        value={mode}
        onChange={handleChange}
        size={size}
        ariaLabel="Edit or audit mode"
      />
    </Box>
  );
};
